import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Skeleton, styled, Typography } from '@mui/material';

import { ReconciliationApi } from '@/api/domains/reconciliation.api';
import { ReconciliationQueryKeys } from '@/api/domains/reconciliation.query-keys';
import type { BreadcrumbComponentProps } from '@/shared/lib/react-router';
import { OrdersPaths } from '@/shared/lib/react-router';

import { useReconciliationOrderGuid } from '../reconciliation-file.store';

const StyledLink = styled(RouterLink)(({ theme }) => ({
  ...theme.typography['Title/Bold/medium'],
  color: theme.palette.neutral['700'],
  textDecoration: 'none',
  whiteSpace: 'nowrap',

  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const ReconciliationBindingBreadcrumb = (_props: BreadcrumbComponentProps) => {
  const { t } = useTranslation('orders-page');
  const orderGuid = useReconciliationOrderGuid();

  const { data: documentNumber, isLoading } = useQuery({
    queryKey: ReconciliationQueryKeys.documentDetails(orderGuid),
    queryFn: () => ReconciliationApi.fetchDocumentDetails(orderGuid),
    enabled: Boolean(orderGuid),
    select: data => data.order.document_number,
  });

  return (
    <Fragment>
      {isLoading ? (
        <Skeleton variant="text" width={120} />
      ) : (
        <StyledLink to={OrdersPaths.ROOT}>{documentNumber}</StyledLink>
      )}
      <KeyboardArrowRightIcon sx={{ color: 'neutral.500', mx: 1 }} />
      <Typography color="neutral.900" variant="Title/Bold/medium" sx={{ whiteSpace: 'nowrap' }}>
        {t('reconciliation.binding.page-title')}
      </Typography>
    </Fragment>
  );
};
